import React, { useEffect, useState, useRef } from 'react';
import { MapPinIcon, PlayIcon, StopCircleIcon, CheckCircleIcon, ClockIcon, NavigationIcon, AlertCircleIcon, BuildingIcon, PhoneIcon, UserIcon, DollarSignIcon, ShoppingCartIcon, MessageSquareIcon, ChevronRightIcon, WifiIcon, ImageIcon, XIcon, FileTextIcon, TagIcon, PlusCircleIcon, PackageIcon, SearchIcon, TrashIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Visit, VisitType, PaymentMethod, Invoice, Product, DevolucionItem, ProductPromotion, Cheque } from '../types';
import { supabaseAdmin } from '../lib/supabase';
const visitTypeConfig: Record<VisitType, {
  label: string;
  color: string;
  bg: string;
}> = {
  cobro: {
    label: 'Cobro',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100'
  },
  venta: {
    label: 'Venta',
    color: 'text-blue-700',
    bg: 'bg-blue-100'
  },
  cobro_y_venta: {
    label: 'Cobro y Venta',
    color: 'text-indigo-700',
    bg: 'bg-indigo-100'
  },
  seguimiento: {
    label: 'Seguimiento',
    color: 'text-amber-700',
    bg: 'bg-amber-100'
  }
};
const emptyCheque: Cheque = {
  numeroCheque: '',
  bancoCheque: '',
  fechaDeposito: '',
  chequeImagen: null
};
const emptyForm = {
  huboCobo: false,
  metodoPago: '' as PaymentMethod | '',
  paymentAllocations: {} as Record<string, string>,
  discountApplied: {} as Record<string, boolean>,
  ppOverrides: {} as Record<string, string>,
  huboVenta: false,
  montoVenta: '',
  numeroPedido: '',
  comentarios: '',
  motivoNoPago: '',
  proximaVisita: '',
  cheques: [{
    ...emptyCheque
  }] as Cheque[],
  productosVencidos: null as boolean | null,
  devolucionItems: [] as DevolucionItem[],
  numeroFacturaManual: ''
};
// ── Haversine distance formula (returns meters) ──────────────────────────────
function getDistanceMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371000; // Earth radius in meters
  const toRad = (deg: number) => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
const GEOFENCE_RADIUS_METERS = 100; // meters — adjust as needed
const FORM_STORAGE_PREFIX = 'mr_visit_form_';
// Helper to get storage key for a visit
function getFormStorageKey(visitId: string): string {
  return `${FORM_STORAGE_PREFIX}${visitId}`;
}
// Helper to save form data to sessionStorage (strips large binary data like cheque images)
function saveFormToStorage(visitId: string, data: typeof emptyForm) {
  try {
    // Strip cheque images to avoid exceeding storage quota
    const toSave = {
      ...data,
      cheques: data.cheques.map(c => ({
        ...c,
        chequeImagen: null
      }))
    };
    sessionStorage.setItem(getFormStorageKey(visitId), JSON.stringify(toSave));
  } catch {
    // Silently fail if storage is full
  }
}
// Helper to restore form data from sessionStorage
function restoreFormFromStorage(visitId: string): typeof emptyForm | null {
  try {
    const raw = sessionStorage.getItem(getFormStorageKey(visitId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Ensure cheques array has proper structure
    if (parsed.cheques) {
      parsed.cheques = parsed.cheques.map((c: any) => ({
        ...emptyCheque,
        ...c
      }));
    }
    return {
      ...emptyForm,
      ...parsed
    };
  } catch {
    return null;
  }
}
// Helper to clear form data from sessionStorage
function clearFormStorage(visitId: string) {
  try {
    sessionStorage.removeItem(getFormStorageKey(visitId));
  } catch {
    // ignore
  }
}
export function NewVisitPage() {
  const {
    user,
    addActivityLog,
    addNotification
  } = useAuth();
  const {
    clients,
    startVisit,
    endVisit,
    visits,
    getCurrentLocation,
    refreshData,
    addClient
  } = useApp();
  const activeVisit = visits.find(v => v.vendedorId === user?.id && v.estado === 'en_progreso');
  const [selectedClient, setSelectedClient] = useState('');
  const [visitType, setVisitType] = useState<VisitType>('cobro_y_venta');
  const [currentVisit, setCurrentVisit] = useState<Visit | null>(activeVisit || null);
  const [isNewClient, setIsNewClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientZona, setNewClientZona] = useState('');
  const [newClientDireccion, setNewClientDireccion] = useState('');
  const [newClientTelefono, setNewClientTelefono] = useState('');
  const [newClientCodigo, setNewClientCodigo] = useState('');
  const [newClientRnc, setNewClientRnc] = useState('');
  const [newClientCedula, setNewClientCedula] = useState('');
  const [newClientCiudad, setNewClientCiudad] = useState('');
  const [newClientRuta, setNewClientRuta] = useState('');
  const [newClientLimite, setNewClientLimite] = useState('');
  const [newClientDependencia, setNewClientDependencia] = useState('');
  const [isCreatingClient, setIsCreatingClient] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [showAbandonConfirm, setShowAbandonConfirm] = useState(false);
  const isCancelledRef = useRef(false);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [formData, setFormData] = useState({
    ...emptyForm
  });
  const [clientInvoices, setClientInvoices] = useState<Invoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  // NC amounts applied per invoice (invoiceId → total NC monto applied)
  const [ncAppliedMap, setNcAppliedMap] = useState<Record<string, number>>({});
  // Devolucion (product returns) state
  const [inventoryProducts, setInventoryProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [devolucionSearch, setDevolucionSearch] = useState('');
  const [showDevolucionDropdown, setShowDevolucionDropdown] = useState(false);
  // Promociones state
  const [promotions, setPromotions] = useState<ProductPromotion[]>([]);
  // Discount config from Supabase (with localStorage fallback)
  const [discountPct, setDiscountPct] = useState(() => parseFloat(localStorage.getItem('mr_discount_pct') || '2'));
  const [discountDays, setDiscountDays] = useState(() => parseInt(localStorage.getItem('mr_discount_days') || '60', 10));
  // ── Restore form data from sessionStorage when returning to an active visit ──
  const skipNextSaveRef = useRef(false);
  useEffect(() => {
    const visit = activeVisit || currentVisit;
    if (!visit || visit.id.startsWith('temp-')) return;
    const saved = restoreFormFromStorage(visit.id);
    if (saved) {
      skipNextSaveRef.current = true; // Don't re-save what we just loaded
      setFormData(saved);
    }
  }, []); // Only on mount
  // ── Persist form data to sessionStorage on every change ──────────────────────
  useEffect(() => {
    if (skipNextSaveRef.current) {
      skipNextSaveRef.current = false;
      return;
    }
    const visit = activeVisit || currentVisit;
    if (!visit || visit.id.startsWith('temp-')) return;
    saveFormToStorage(visit.id, formData);
  }, [formData]);
  useEffect(() => {
    const loadDiscount = async () => {
      try {
        const {
          data
        } = await supabaseAdmin.from('app_settings').select('key, value').in('key', ['discount_pct', 'discount_days']);
        if (data && data.length > 0) {
          const pctRow = data.find((r: any) => r.key === 'discount_pct');
          const daysRow = data.find((r: any) => r.key === 'discount_days');
          const pct = pctRow ? parseFloat(pctRow.value) : parseFloat(localStorage.getItem('mr_discount_pct') || '2');
          const days = daysRow ? parseInt(daysRow.value, 10) : parseInt(localStorage.getItem('mr_discount_days') || '60', 10);
          setDiscountPct(pct);
          setDiscountDays(days);
          localStorage.setItem('mr_discount_pct', String(pct));
          localStorage.setItem('mr_discount_days', String(days));
        }
      } catch {
        // Fallback to localStorage
      }
    };
    loadDiscount();
    // Re-read if config changes from CuentasCobrarPage
    const handleUpdate = (e: any) => {
      if (e.detail) {
        setDiscountPct(e.detail.pct);
        setDiscountDays(e.detail.days);
      } else {
        loadDiscount();
      }
    };
    window.addEventListener('discount-config-updated', handleUpdate);
    window.addEventListener('storage', loadDiscount);
    return () => {
      window.removeEventListener('discount-config-updated', handleUpdate);
      window.removeEventListener('storage', loadDiscount);
    };
  }, []);
  // Fetch active promotions on load
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        const {
          data
        } = await supabaseAdmin.from('product_promotions').select('*').eq('activo', true).or(`fecha_fin.is.null,fecha_fin.gte.${new Date().toISOString().split('T')[0]}`);
        if (data) {
          setPromotions(data.map((row: any) => ({
            id: row.id,
            productId: row.product_id,
            productName: row.product_name,
            productSku: row.product_sku,
            tipo: row.tipo,
            descuentoPct: row.descuento_pct,
            precioOferta: row.precio_oferta,
            activo: row.activo,
            fechaInicio: new Date(row.fecha_inicio),
            fechaFin: row.fecha_fin ? new Date(row.fecha_fin) : null,
            notas: row.notas
          })));
        }
      } catch (err) {
        console.error('Error loading promotions:', err);
      }
    };
    fetchPromotions();
  }, []);
  // Fetch inventory products when productosVencidos becomes true
  useEffect(() => {
    if (formData.productosVencidos === true && inventoryProducts.length === 0) {
      const fetchProducts = async () => {
        setIsLoadingProducts(true);
        try {
          const allProducts: Product[] = [];
          const PAGE_SIZE = 1000;
          let from = 0;
          let hasMore = true;
          while (hasMore) {
            const {
              data,
              error
            } = await supabaseAdmin.from('products').select('*').order('nombre').range(from, from + PAGE_SIZE - 1);
            if (error) break;
            if (data && data.length > 0) {
              allProducts.push(...data.map((row: any): Product => ({
                id: row.id,
                nombre: row.nombre,
                sku: row.sku,
                stock: row.stock,
                stockMinimo: row.stock_minimo ?? 0,
                precio: row.precio,
                proveedor: row.proveedor
              })));
              from += PAGE_SIZE;
              hasMore = data.length === PAGE_SIZE;
            } else {
              hasMore = false;
            }
          }
          setInventoryProducts(allProducts);
        } finally {
          setIsLoadingProducts(false);
        }
      };
      fetchProducts();
    }
  }, [formData.productosVencidos]);
  // Check if invoice is eligible for discount (pronto pago)
  const isDiscountEligible = (inv: Invoice) => {
    // Pagadas nunca son elegibles
    if (inv.estado === 'pagada') return false;
    // Si tiene PP% explícito (asignado en CxC o inline), SIEMPRE es elegible
    // aunque esté vencida — el admin decidió darle descuento
    const hasExplicitPP = inv.ppPct != null || formData.ppOverrides[inv.id] !== undefined && formData.ppOverrides[inv.id] !== '';
    if (hasExplicitPP) return true;
    // Facturas vencidas sin PP explícito NO son elegibles
    if (inv.estado === 'vencida') return false;
    // Elegible si la fecha de vencimiento aún no ha pasado
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const fechaVenc = new Date(inv.fechaVencimiento);
    fechaVenc.setHours(0, 0, 0, 0);
    return fechaVenc >= today;
  };
  // When discount is toggled — just toggle the flag, don't change the abono
  // The user enters whatever amount they want to pay, discount applies on top
  const toggleDiscount = (invId: string, checked: boolean, inv: Invoice) => {
    setFormData(prev => ({
      ...prev,
      discountApplied: {
        ...prev.discountApplied,
        [invId]: checked
      }
    }));
  };
  // What the client physically pays in cash
  // Descuento se aplica sobre el subtotal AJUSTADO (después de NC, sin ITBIS)
  const getEffectiveAbono = (inv: Invoice) => {
    const abono = parseFloat(formData.paymentAllocations[inv.id] || '0');
    if (formData.discountApplied[inv.id] && isDiscountEligible(inv) && abono > 0) {
      const pp = getInvoicePP(inv);
      const adjustedSubTotal = getAdjustedSubTotal(inv);
      const descuento = Math.round(adjustedSubTotal * (pp / 100) * 100) / 100;
      return Math.round((abono - descuento) * 100) / 100;
    }
    return abono;
  };
  // How much the saldo is reduced = lo que el cliente paga (ya con el descuento aplicado correctamente)
  const getSaldoReduction = (inv: Invoice) => {
    const abono = parseFloat(formData.paymentAllocations[inv.id] || '0');
    if (formData.discountApplied[inv.id] && isDiscountEligible(inv) && abono > 0) {
      // El saldo se reduce por el abono original (no el descontado)
      // porque el descuento es lo que la empresa "regala" del saldo
      return Math.min(abono, inv.saldoPendiente);
    }
    return abono;
  };
  // Get the effective PP% for a specific invoice:
  // 1. Check local override (edited inline during this visit)
  // 2. Check per-invoice DB value (inv.ppPct)
  // 3. Fall back to global discountPct
  const getInvoicePP = (inv: Invoice): number => {
    const override = formData.ppOverrides[inv.id];
    if (override !== undefined && override !== '') return parseFloat(override) || 0;
    return inv.ppPct != null ? inv.ppPct : discountPct;
  };
  // Get the adjusted subtotal for an invoice (original subtotal minus any NC applied)
  const getAdjustedSubTotal = (inv: Invoice): number => {
    const ncAmount = ncAppliedMap[inv.id] || 0;
    return Math.max(0, inv.subTotal - ncAmount);
  };
  // Discount amount — se aplica sobre el subTotal AJUSTADO (después de NC, sin ITBIS)
  const getDiscountAmount = (inv: Invoice) => {
    const abono = parseFloat(formData.paymentAllocations[inv.id] || '0');
    if (formData.discountApplied[inv.id] && isDiscountEligible(inv) && abono > 0) {
      const pp = getInvoicePP(inv);
      const adjustedSubTotal = getAdjustedSubTotal(inv);
      return Math.round(adjustedSubTotal * (pp / 100) * 100) / 100;
    }
    return 0;
  };
  // Total a cobrar = sum of cash actually received (abono - discount per invoice)
  const totalCobrado = clientInvoices.reduce((sum, inv) => sum + getEffectiveAbono(inv), 0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (currentVisit?.horaLlegada) {
      interval = setInterval(() => {
        const start = new Date(currentVisit.horaLlegada!).getTime();
        setElapsedTime(Math.floor((Date.now() - start) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [currentVisit]);
  useEffect(() => {
    if (isCancelledRef.current) {
      // Visit was definitively closed — don't re-set from stale activeVisit
      if (!activeVisit) isCancelledRef.current = false; // Reset flag once AppContext caught up
      return;
    }
    if (activeVisit && !currentVisit) setCurrentVisit(activeVisit);
  }, [activeVisit]);
  useEffect(() => {
    if (isCancelledRef.current) return;
    if (activeVisit) setCurrentVisit(activeVisit);
  }, [activeVisit?.id]);
  useEffect(() => {
    const farmacia = currentVisit?.clienteNombre;
    if (!farmacia) {
      setClientInvoices([]);
      return;
    }
    const fetchInvoices = async () => {
      setIsLoadingInvoices(true);
      try {
        const {
          data
        } = await supabaseAdmin.from('invoices').select('*').eq('farmacia', farmacia).neq('estado', 'pagada').order('fecha_vencimiento', {
          ascending: true
        }).limit(500);
        if (data) {
          const mapped = data.map((row: any): Invoice => ({
            id: row.id,
            numeroFactura: row.numero_factura,
            farmacia: row.farmacia,
            vendedor: row.vendedor,
            fechaEmision: new Date(row.fecha_emision),
            fechaVencimiento: new Date(row.fecha_vencimiento),
            subTotal: row.sub_total || 0,
            itbis: row.itbis || 0,
            monto: row.monto,
            saldoPendiente: row.saldo_pendiente,
            estado: row.estado,
            ppPct: row.pp_pct != null ? Number(row.pp_pct) : null
          }));
          // Ordenar de más antigua a más nueva (fecha de emisión)
          mapped.sort((a, b) => new Date(a.fechaEmision).getTime() - new Date(b.fechaEmision).getTime());
          setClientInvoices(mapped);
          // Fetch NC applications for these invoices to compute adjusted subtotals
          const invoiceIds = mapped.map(inv => inv.id);
          if (invoiceIds.length > 0) {
            try {
              const {
                data: ncApps
              } = await supabaseAdmin.from('nc_applications').select('invoice_id, nc_monto_aplicado').in('invoice_id', invoiceIds);
              if (ncApps && ncApps.length > 0) {
                const ncMap: Record<string, number> = {};
                ncApps.forEach((nc: any) => {
                  const invId = nc.invoice_id;
                  ncMap[invId] = (ncMap[invId] || 0) + Number(nc.nc_monto_aplicado || 0);
                });
                setNcAppliedMap(ncMap);
              } else {
                setNcAppliedMap({});
              }
            } catch {
              setNcAppliedMap({});
            }
          } else {
            setNcAppliedMap({});
          }
        }
      } catch {
        setClientInvoices([]);
      } finally {
        setIsLoadingInvoices(false);
      }
    };
    fetchInvoices();
  }, [currentVisit?.clienteNombre]);
  const formatElapsedTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor(seconds % 3600 / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  const handleStartVisit = async () => {
    if (!user) return;
    // If new client mode, create the client first
    if (isNewClient && visitType === 'seguimiento') {
      if (!newClientName.trim()) return;
      setIsCreatingClient(true);
      setLocationError('');
      try {
        const result = await addClient({
          codigo: newClientCodigo.trim(),
          nombre: newClientName.trim(),
          telefono: newClientTelefono.trim(),
          ciudad: newClientCiudad.trim(),
          limite: parseFloat(newClientLimite) || 0,
          cedula: newClientCedula.trim(),
          rnc: newClientRnc.trim(),
          ruta: newClientRuta.trim(),
          direccion: newClientDireccion.trim(),
          vendedorId: user.id,
          vendedorNombre: user.nombre,
          dependencia: newClientDependencia.trim(),
          zona: newClientZona.trim() || 'General',
          lat: null,
          lng: null,
          contacto: ''
        });
        if (!result.success) {
          setLocationError(result.error || 'Error al crear el cliente');
          return;
        }
        // Wait a moment for the client list to refresh via realtime
        await new Promise(r => setTimeout(r, 1500));
        // Find the newly created client
        const {
          data: newClients
        } = await supabaseAdmin.from('clients').select('id').eq('nombre', newClientName.trim()).order('created_at', {
          ascending: false
        }).limit(1);
        const newClientId = newClients?.[0]?.id;
        if (!newClientId) {
          setLocationError('Cliente creado pero no se pudo iniciar la visita. Intente seleccionarlo de la lista.');
          return;
        }
        const visit = startVisit(user.id, user.nombre, newClientId, visitType);
        setCurrentVisit(visit);
        addActivityLog('Inicio de visita', `Cliente nuevo: ${newClientName.trim()} | Tipo: ${visitType}`);
        // Reset new client fields
        setIsNewClient(false);
        setNewClientName('');
        setNewClientZona('');
        setNewClientDireccion('');
        setNewClientTelefono('');
        setNewClientCodigo('');
        setNewClientRnc('');
        setNewClientCedula('');
        setNewClientCiudad('');
        setNewClientRuta('');
        setNewClientLimite('');
        setNewClientDependencia('');
      } catch {
        setLocationError('Error al crear el cliente. Intente nuevamente.');
      } finally {
        setIsCreatingClient(false);
      }
      return;
    }
    // Normal flow — existing client
    if (!selectedClient) return;
    setLocationError('');
    try {
      const visit = startVisit(user.id, user.nombre, selectedClient, visitType);
      setCurrentVisit(visit);
      addActivityLog('Inicio de visita', `Cliente: ${visit.clienteNombre} | Tipo: ${visitType}`);
    } catch {
      setLocationError('Error al iniciar la visita. Intente nuevamente.');
    }
  };
  const handleEndVisit = async () => {
    const visitToEnd = activeVisit || currentVisit;
    if (!visitToEnd) return;
    if (visitToEnd.id.startsWith('temp-')) {
      setLocationError('La visita aún se está guardando, intente en un momento...');
      setTimeout(() => setLocationError(''), 3000);
      return;
    }
    setIsGettingLocation(true);
    // Validar método de pago si hubo cobro
    if (formData.huboCobo && !formData.metodoPago) {
      setLocationError('Debes seleccionar un método de pago antes de finalizar la visita.');
      setIsGettingLocation(false);
      // Scroll to the cobro section so the user sees the payment method field
      setTimeout(() => {
        document.querySelector('[data-section="cobro"]')?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
      return;
    }
    // Validar pregunta obligatoria de productos vencidos
    if (formData.productosVencidos === null) {
      setLocationError('Debes indicar si el cliente tiene productos vencidos antes de finalizar.');
      setIsGettingLocation(false);
      return;
    }
    try {
      const allocatedInvoices = clientInvoices.filter(inv => parseFloat(formData.paymentAllocations[inv.id] || '0') > 0);
      const manualAmount = parseFloat(formData.paymentAllocations['__manual__'] || '0');
      const facturasList = formData.numeroFacturaManual.trim() || allocatedInvoices.map(inv => inv.numeroFactura).join(', ');
      // Fetch vendor commission config
      const {
        data: commConfig
      } = await supabaseAdmin.from('vendor_commissions').select('*').eq('vendor_id', user.id).single();
      const pctVenta = commConfig?.comision_venta_pct || 0;
      const pctCobro = commConfig?.comision_cobro_pct || 0;
      // Total cash received (abono minus discount per invoice)
      const finalTotal = allocatedInvoices.length > 0 ? allocatedInvoices.reduce((sum, inv) => sum + getEffectiveAbono(inv), 0) : manualAmount;
      // Total crédito = sum of raw abonos entered (before discount deduction)
      const totalCredito = allocatedInvoices.length > 0 ? allocatedInvoices.reduce((sum, inv) => sum + parseFloat(formData.paymentAllocations[inv.id] || '0'), 0) : manualAmount;
      // Total descuento = sum of discount amounts gifted across all invoices
      const totalDescuento = allocatedInvoices.reduce((sum, inv) => sum + getDiscountAmount(inv), 0);
      // Detalle individual por factura
      const facturasDetalle = allocatedInvoices.map(inv => {
        const abono = parseFloat(formData.paymentAllocations[inv.id] || '0');
        const discApplied = formData.discountApplied[inv.id] && isDiscountEligible(inv);
        const descuento = getDiscountAmount(inv);
        const efectivoPagado = getEffectiveAbono(inv);
        const saldoReduction = getSaldoReduction(inv);
        const saldoNuevo = Math.max(0, inv.saldoPendiente - saldoReduction);
        const estadoNuevo = saldoNuevo <= 0 ? 'pagada' : saldoNuevo < inv.monto ? 'parcial' : 'pendiente';
        return {
          facturaId: inv.id,
          numeroFactura: inv.numeroFactura,
          saldoAnterior: inv.saldoPendiente,
          abono,
          descuentoAplicado: discApplied,
          descuento,
          efectivoPagado,
          saldoNuevo,
          estado: estadoNuevo,
          subTotal: inv.subTotal || 0,
          itbis: inv.itbis || 0
        };
      });
      // NOTE: Commission records are inserted ONCE in the batch block below.
      // Do NOT insert them here to avoid duplication.
      const visitData: Partial<Visit> = {
        cobro: {
          huboCobo: formData.huboCobo,
          montoCobrado: finalTotal,
          metodoPago: formData.metodoPago as PaymentMethod || null,
          numeroFactura: facturasList,
          estadoFactura: finalTotal > 0 ? 'pagada_completa' : 'no_pago',
          chequeImagen: formData.cheques[0]?.chequeImagen || null,
          numeroCheque: formData.cheques[0]?.numeroCheque || '',
          fechaDeposito: formData.cheques[0]?.fechaDeposito || '',
          bancoCheque: formData.cheques[0]?.bancoCheque || '',
          cheques: formData.cheques.filter(c => c.numeroCheque || c.chequeImagen),
          credito: totalCredito,
          descuento: totalDescuento,
          facturasDetalle
        },
        venta: {
          huboVenta: formData.huboVenta,
          montoVenta: parseFloat(formData.montoVenta) || 0,
          numeroPedido: formData.numeroPedido
        },
        comentarios: formData.comentarios,
        motivoNoPago: formData.motivoNoPago,
        productosVencidos: formData.productosVencidos,
        devolucionItems: formData.devolucionItems,
        proximaVisita: formData.proximaVisita ? new Date(formData.proximaVisita) : null
      };
      await endVisit(visitToEnd.id, visitData);
      // Update invoice balances
      if (formData.huboCobo && allocatedInvoices.length > 0) {
        await Promise.all(allocatedInvoices.map(async inv => {
          const saldoReduction = getSaldoReduction(inv);
          if (saldoReduction <= 0) return;
          const discApplied = formData.discountApplied[inv.id] && isDiscountEligible(inv);
          const newSaldo = Math.max(0, inv.saldoPendiente - saldoReduction);
          const newEstado = newSaldo <= 0 ? 'pagada' : newSaldo < inv.monto ? 'parcial' : 'pendiente';
          await supabaseAdmin.from('invoices').update({
            saldo_pendiente: newSaldo,
            estado: newEstado,
            ...(discApplied ? {
              descuento_aplicado: true
            } : {})
          }).eq('id', inv.id);
        }));
      }
      // Persist PP% overrides to invoices (even if no payment was made)
      const ppEntries = Object.entries(formData.ppOverrides).filter(([, val]) => val !== '');
      if (ppEntries.length > 0) {
        await Promise.all(ppEntries.map(async ([invId, pctStr]) => {
          const pct = parseFloat(pctStr);
          if (!isNaN(pct)) {
            await supabaseAdmin.from('invoices').update({
              pp_pct: pct
            }).eq('id', invId);
          }
        }));
      }
      // ── COMISIONES: Registrar automáticamente (UNA SOLA VEZ) ───────────────
      if (user && commConfig) {
        try {
          const commissionRecords: any[] = [];
          // Comisión por venta
          if (formData.huboVenta && parseFloat(formData.montoVenta) > 0 && pctVenta > 0) {
            const montoVenta = parseFloat(formData.montoVenta);
            const comisionVenta = Math.round(montoVenta * (pctVenta / 100) * 100) / 100;
            commissionRecords.push({
              vendor_id: user.id,
              vendor_nombre: user.nombre,
              cliente_id: selectedClient || visitToEnd.clienteId,
              cliente_nombre: visitToEnd.clienteNombre,
              tipo: 'venta',
              monto_base: montoVenta,
              porcentaje: pctVenta,
              monto_comision: comisionVenta,
              referencia_id: visitToEnd.id,
              fecha: new Date().toISOString()
            });
          }
          // Comisión por cobro
          if (formData.huboCobo && finalTotal > 0 && pctCobro > 0) {
            const comisionCobro = Math.round(finalTotal * (pctCobro / 100) * 100) / 100;
            commissionRecords.push({
              vendor_id: user.id,
              vendor_nombre: user.nombre,
              cliente_id: selectedClient || visitToEnd.clienteId,
              cliente_nombre: visitToEnd.clienteNombre,
              tipo: 'cobro',
              monto_base: finalTotal,
              porcentaje: pctCobro,
              monto_comision: comisionCobro,
              referencia_id: visitToEnd.id,
              fecha: new Date().toISOString()
            });
          }
          if (commissionRecords.length > 0) {
            await supabaseAdmin.from('commission_records').insert(commissionRecords);
          }
        } catch (commErr) {
          console.error('Error registrando comisiones:', commErr);
        }
      }
      // ── DEVOLUCIONES: Devolver al stock + crear Nota de Crédito ────────────
      if (formData.devolucionItems && formData.devolucionItems.length > 0) {
        try {
          const validItems = formData.devolucionItems.filter(item => item.cantidad > 0 && item.productoId);
          if (validItems.length > 0) {
            // 1. Update stock — add returned quantities back to inventory
            const productIds = validItems.map(item => item.productoId);
            const {
              data: currentProducts
            } = await supabaseAdmin.from('products').select('id, stock').in('id', productIds);
            if (currentProducts) {
              await Promise.all(validItems.map(async item => {
                const currentProduct = currentProducts.find((p: any) => p.id === item.productoId);
                if (currentProduct) {
                  const newStock = (currentProduct.stock || 0) + item.cantidad;
                  await supabaseAdmin.from('products').update({
                    stock: newStock
                  }).eq('id', item.productoId);
                }
              }));
            }
            // 2. Calculate total devolucion value
            const totalDevolucionValue = validItems.reduce((sum, item) => sum + item.cantidad * (item.precioUnitario || 0), 0);
            // 3. Create credit note if there's monetary value
            if (totalDevolucionValue > 0) {
              const ts = Date.now().toString(36).toUpperCase();
              const rnd = Math.random().toString(36).slice(2, 6).toUpperCase();
              const ncNumero = `NC-DEV-${ts}-${rnd}`;
              const productosDesc = validItems.map(item => `${item.productoNombre} x${item.cantidad}`).join(', ');
              const ncPayload = {
                numero: ncNumero,
                farmacia: visitToEnd.clienteNombre,
                vendedor: user?.nombre || visitToEnd.vendedorNombre,
                vendedor_id: user?.id || visitToEnd.vendedorId,
                monto: Math.round(totalDevolucionValue * 100) / 100,
                monto_disponible: Math.round(totalDevolucionValue * 100) / 100,
                estado: 'disponible',
                notas: `Devolución de productos vencidos: ${productosDesc}`
              };
              let createdNC: any = null;
              const {
                data: ncData,
                error: ncError
              } = await supabaseAdmin.from('credit_notes').insert(ncPayload).select().single();
              if (ncError) {
                // Retry once with a different numero in case of collision
                console.error('NC insert error, retrying:', ncError.message);
                const rnd2 = Math.random().toString(36).slice(2, 8).toUpperCase();
                ncPayload.numero = `NC-DEV-${ts}-${rnd2}`;
                const {
                  data: ncData2,
                  error: ncError2
                } = await supabaseAdmin.from('credit_notes').insert(ncPayload).select().single();
                if (ncError2) {
                  console.error('NC insert retry failed:', ncError2.message);
                } else {
                  createdNC = ncData2;
                }
              } else {
                createdNC = ncData;
              }
              // NC queda como "disponible" — se aplica manualmente desde Cuentas por Cobrar
            }
          }
        } catch (devErr) {
          // Don't block visit completion if stock/credit note update fails
          console.error('Error procesando devolución:', devErr);
        }
      }
      addActivityLog('Fin de visita', `Cliente: ${visitToEnd.clienteNombre} | Duración: ${Math.floor(elapsedTime / 60)} min`);
      addNotification('visita_completada', 'Visita completada', `${visitToEnd.clienteNombre} — ${Math.floor(elapsedTime / 60)} min`);
      if (formData.huboCobo && finalTotal > 0) addNotification('nuevo_cobro', 'Nuevo cobro registrado', `${visitToEnd.clienteNombre} — RD$${finalTotal.toLocaleString()}`);
      if (formData.huboVenta && formData.montoVenta) addNotification('nueva_venta', 'Nueva venta registrada', `${visitToEnd.clienteNombre} — RD$${parseFloat(formData.montoVenta).toLocaleString()}`);
      // Clear persisted form data
      clearFormStorage(visitToEnd.id);
      setCurrentVisit(null);
      setSelectedClient('');
      setClientInvoices([]);
      setFormData({
        ...emptyForm
      });
      setElapsedTime(0);
    } finally {
      setIsGettingLocation(false);
    }
  };
  const handleAbandonVisit = () => {
    // NOTE: We do NOT clear storage here — the user wants to come back later
    setCurrentVisit(null);
    setSelectedClient('');
    setClientInvoices([]);
    setFormData({
      ...emptyForm
    });
    setElapsedTime(0);
    setShowAbandonConfirm(false);
  };
  // Closes the visit permanently — marks it as 'cancelada' in the DB
  const handleCloseDefinitively = async () => {
    const visitToClose = activeVisit || currentVisit;
    if (!visitToClose || visitToClose.id.startsWith('temp-')) {
      handleAbandonVisit();
      return;
    }
    // Set flag BEFORE clearing state to prevent useEffects from re-setting currentVisit
    isCancelledRef.current = true;
    try {
      await supabaseAdmin.from('visits').update({
        estado: 'cancelada'
      }).eq('id', visitToClose.id);
      addActivityLog('Canceló visita', `Cliente: ${visitToClose.clienteNombre} | Tipo: ${visitToClose.tipoVisita}`);
      // Refresh AppContext so activeVisit no longer picks up this visit
      await refreshData();
    } catch {
      // Continue even if DB update fails
    }
    // Clear persisted form data since visit is cancelled
    if (visitToClose) clearFormStorage(visitToClose.id);
    setCurrentVisit(null);
    setSelectedClient('');
    setClientInvoices([]);
    setFormData({
      ...emptyForm
    });
    setElapsedTime(0);
    setShowAbandonConfirm(false);
  };
  const clientOptions = clients.map(c => ({
    value: c.id,
    label: c.zona && c.zona !== 'null' ? `${c.nombre} - ${c.zona}` : c.nombre
  }));
  const selectedClientData = clients.find(c => c.id === selectedClient);
  const visitTypeOptions = [{
    value: 'cobro',
    label: 'Cobro'
  }, {
    value: 'venta',
    label: 'Venta'
  }, {
    value: 'cobro_y_venta',
    label: 'Cobro y Venta'
  }, {
    value: 'seguimiento',
    label: 'Seguimiento'
  }];
  const paymentMethodOptions = [{
    value: 'efectivo',
    label: 'Efectivo'
  }, {
    value: 'transferencia',
    label: 'Transferencia'
  }, {
    value: 'cheque',
    label: 'Cheque'
  }];
  // ── ACTIVE VISIT VIEW ──────────────────────────────────────────────────────
  if (currentVisit) {
    return <div className="max-w-3xl mx-auto space-y-4 sm:space-y-5" data-id="element-2297">
        {/* Visit header */}
        <div className="relative overflow-hidden bg-slate-900 rounded-xl sm:rounded-2xl shadow-xl" data-id="element-2298">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/30 via-transparent to-emerald-600/20" data-id="element-2299" />
          <div className="relative p-4 sm:p-6" data-id="element-2300">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4" data-id="element-2301">
              <div className="flex items-center gap-3 sm:gap-4 min-w-0" data-id="element-2302">
                <div className="w-11 h-11 sm:w-14 sm:h-14 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center border border-white/20 flex-shrink-0" data-id="element-2303">
                  <BuildingIcon className="w-6 h-6 sm:w-7 sm:h-7 text-white" data-id="element-2304" />
                </div>
                <div className="min-w-0" data-id="element-2305">
                  <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap" data-id="element-2306">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-full text-[10px] sm:text-xs font-bold text-emerald-400" data-id="element-2307">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-2308" />{' '}
                      En Progreso
                    </span>
                    <span className={`px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${visitTypeConfig[currentVisit.tipoVisita].bg} ${visitTypeConfig[currentVisit.tipoVisita].color}`} data-id="element-2309">
                      {visitTypeConfig[currentVisit.tipoVisita].label}
                    </span>
                  </div>
                  <h2 className="text-base sm:text-xl font-bold text-white truncate" data-id="element-2310">
                    {currentVisit.clienteNombre}
                  </h2>
                  <p className="text-slate-400 text-xs sm:text-sm mt-0.5 truncate" data-id="element-2311">
                    {currentVisit.zona}
                  </p>
                </div>
              </div>
              <div className="flex items-center sm:items-end sm:flex-col gap-3 justify-between sm:justify-start flex-shrink-0" data-id="element-2312">
                <div className="sm:text-right" data-id="element-2313">
                  <p className="text-slate-400 text-[10px] sm:text-xs mb-0.5 sm:mb-1 flex items-center gap-1 sm:justify-end" data-id="element-2314">
                    <ClockIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-2315" /> Tiempo
                  </p>
                  <p className="text-xl sm:text-3xl font-mono font-bold text-white tracking-wider" data-id="element-2316">
                    {formatElapsedTime(elapsedTime)}
                  </p>
                </div>
                <button onClick={() => setShowAbandonConfirm(true)} className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg text-[10px] sm:text-xs font-medium text-white/70 hover:text-white transition-all whitespace-nowrap" data-id="element-2317">
                  <XIcon className="w-3 h-3 sm:w-3.5 sm:h-3.5" data-id="element-2318" /> Salir
                </button>
              </div>
            </div>
          </div>
        </div>

        {locationError && <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-2xl text-sm text-rose-700" data-id="element-2319">
            <AlertCircleIcon className="w-5 h-5 flex-shrink-0" data-id="element-2320" />
            {locationError}
          </div>}

        {/* Productos Destacados Banner */}
        {promotions.length > 0 && (currentVisit.tipoVisita === 'venta' || currentVisit.tipoVisita === 'cobro_y_venta') && <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl border border-indigo-100 shadow-sm overflow-hidden" data-id="element-2321">
              <div className="px-5 py-3 border-b border-indigo-100 flex items-center gap-2" data-id="element-2322">
                <TagIcon className="w-5 h-5 text-indigo-600" data-id="element-2323" />
                <h3 className="font-bold text-indigo-900" data-id="element-2324">
                  Productos Destacados (Ofrecer a cliente)
                </h3>
              </div>
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2325">
                {promotions.map(promo => <div key={promo.id} className="bg-white p-3 rounded-xl border border-indigo-50 shadow-sm flex items-start gap-3" data-id="element-2326">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${promo.tipo === 'oferta' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-2327">
                      <PackageIcon className="w-5 h-5" data-id="element-2328" />
                    </div>
                    <div data-id="element-2329">
                      <div className="flex items-center gap-2 mb-0.5" data-id="element-2330">
                        <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${promo.tipo === 'oferta' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-2331">
                          {promo.tipo}
                        </span>
                        {(promo.descuentoPct > 0 || promo.precioOferta > 0) && <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded" data-id="element-2332">
                            {promo.descuentoPct > 0 ? `${promo.descuentoPct}% DESC` : `RD$${promo.precioOferta}`}
                          </span>}
                      </div>
                      <p className="font-semibold text-slate-900 text-sm leading-tight" data-id="element-2333">
                        {promo.productName}
                      </p>
                      {promo.notas && <p className="text-xs text-slate-500 mt-1 italic" data-id="element-2334">
                          "{promo.notas}"
                        </p>}
                    </div>
                  </div>)}
              </div>
            </div>}

        {/* Cobro section */}
        {(currentVisit.tipoVisita === 'cobro' || currentVisit.tipoVisita === 'cobro_y_venta') && <div data-section="cobro" className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-2335">
            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-emerald-50 to-white border-b border-slate-100" data-id="element-2336">
              <div className="w-9 h-9 bg-emerald-100 rounded-xl flex items-center justify-center" data-id="element-2337">
                <DollarSignIcon className="w-5 h-5 text-emerald-600" data-id="element-2338" />
              </div>
              <div data-id="element-2339">
                <h3 className="font-bold text-slate-900" data-id="element-2340">Gestión de Cobro</h3>
                <p className="text-xs text-slate-500" data-id="element-2341">
                  Registra el cobro realizado
                </p>
              </div>
            </div>
            <div className="p-6 space-y-5" data-id="element-2342">
              {/* Invoices display */}
              <div data-id="element-2343">
                <div className="flex items-center gap-2 mb-3" data-id="element-2344">
                  <FileTextIcon className="w-4 h-4 text-slate-400" data-id="element-2345" />
                  <span className="text-sm font-semibold text-slate-700" data-id="element-2346">
                    Cuentas por Cobrar — {currentVisit.clienteNombre}
                  </span>
                </div>
                {isLoadingInvoices ? <div className="flex items-center gap-2 px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-500" data-id="element-2347">
                    <div className="w-4 h-4 border-2 border-slate-300 border-t-emerald-500 rounded-full animate-spin flex-shrink-0" data-id="element-2348" />
                    Cargando facturas pendientes...
                  </div> : clientInvoices.length === 0 ? <div className="flex items-center gap-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700" data-id="element-2349">
                    <CheckCircleIcon className="w-4 h-4 flex-shrink-0" data-id="element-2350" /> Esta
                    farmacia no tiene facturas pendientes
                  </div> : <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2351">
                    <div className="overflow-x-auto" data-id="element-2352">
                      <table className="w-full text-sm" data-id="element-2353">
                        <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2354">
                          <tr data-id="element-2355">
                            <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2356">
                              Factura
                            </th>
                            <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2357">
                              Vencimiento
                            </th>
                            <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2358">
                              Sub Total
                            </th>
                            <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2359">
                              ITBIS
                            </th>
                            <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2360">
                              Monto
                            </th>
                            <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2361">
                              Saldo
                            </th>
                            <th className="px-3 py-2.5 text-center text-xs font-semibold text-slate-500 uppercase" data-id="element-2362">
                              Estado
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100" data-id="element-2363">
                          {clientInvoices.map(inv => {
                      const isVencida = inv.estado === 'vencida';
                      const isParcial = inv.estado === 'parcial';
                      const invSubTotal = inv.subTotal || Math.round(inv.monto / 1.18 * 100) / 100;
                      const invItbis = inv.itbis || Math.round((inv.monto - invSubTotal) * 100) / 100;
                      return <tr key={inv.id} className={isVencida ? 'bg-rose-50/40' : 'bg-white'} data-id="element-2364">
                                <td className="px-3 py-2.5 font-semibold text-slate-900" data-id="element-2365">
                                  {inv.numeroFactura}
                                </td>
                                <td className="px-3 py-2.5 text-slate-600" data-id="element-2366">
                                  {new Date(inv.fechaVencimiento).toLocaleDateString('es-DO', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                                </td>
                                <td className="px-3 py-2.5 text-right text-slate-600" data-id="element-2367">
                                  RD${invSubTotal.toLocaleString()}
                                </td>
                                <td className="px-3 py-2.5 text-right text-slate-500" data-id="element-2368">
                                  RD${invItbis.toLocaleString()}
                                </td>
                                <td className="px-3 py-2.5 text-right text-slate-600" data-id="element-2369">
                                  RD${inv.monto.toLocaleString()}
                                </td>
                                <td className="px-3 py-2.5 text-right font-bold text-slate-900" data-id="element-2370">
                                  RD${inv.saldoPendiente.toLocaleString()}
                                </td>
                                <td className="px-3 py-2.5 text-center" data-id="element-2371">
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${isVencida ? 'bg-rose-100 text-rose-700' : isParcial ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-2372">
                                    {isVencida ? 'Vencida' : isParcial ? 'Parcial' : 'Pendiente'}
                                  </span>
                                </td>
                              </tr>;
                    })}
                        </tbody>
                        <tfoot className="bg-slate-50 border-t border-slate-200" data-id="element-2373">
                          <tr data-id="element-2374">
                            <td colSpan={5} className="px-3 py-2.5 text-right text-xs font-bold text-slate-600" data-id="element-2375">
                              Total pendiente:
                            </td>
                            <td className="px-3 py-2.5 text-right text-sm font-bold text-emerald-700" data-id="element-2376">
                              RD$
                              {clientInvoices.reduce((s, i) => s + i.saldoPendiente, 0).toLocaleString()}
                            </td>
                            <td data-id="element-2377" />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>}
              </div>

              {/* ¿Se realizó el cobro? */}
              <label className="flex items-center gap-3 cursor-pointer group" data-id="element-2378">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.huboCobo ? 'bg-emerald-500 border-emerald-500' : 'border-slate-300 group-hover:border-emerald-400'}`} data-id="element-2379">
                  {formData.huboCobo && <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-2380" />}
                  <input type="checkbox" checked={formData.huboCobo} onChange={e => setFormData({
                ...formData,
                huboCobo: e.target.checked,
                paymentAllocations: {},
                discountApplied: {},
                cheques: [{
                  ...emptyCheque
                }]
              })} className="sr-only" data-id="element-2381" />
                </div>
                <span className="font-semibold text-slate-800" data-id="element-2382">
                  ¿Se realizó el cobro?
                </span>
              </label>

              {formData.huboCobo && <div className="space-y-4 pt-1" data-id="element-2383">
                  <Select label="Método de Pago" options={paymentMethodOptions} value={formData.metodoPago} onChange={e => setFormData({
              ...formData,
              metodoPago: e.target.value as PaymentMethod,
              cheques: [{
                ...emptyCheque
              }]
            })} placeholder="Seleccionar..." data-id="element-2384" />

                  {clientInvoices.length > 0 ? <div data-id="element-2385">
                      <label className="block text-sm font-semibold text-slate-700 mb-3" data-id="element-2386">
                        Distribución del Pago
                        <span className="ml-2 text-xs font-normal text-slate-400" data-id="element-2387">
                          — Ingresa el monto a abonar por cada factura
                        </span>
                      </label>
                      <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-2388">
                        <table className="w-full text-sm" data-id="element-2389">
                          <thead className="bg-slate-50 border-b border-slate-200" data-id="element-2390">
                            <tr data-id="element-2391">
                              <th className="px-3 py-2.5 text-left text-xs font-semibold text-slate-500 uppercase" data-id="element-2392">
                                Factura
                              </th>
                              <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase" data-id="element-2393">
                                Saldo
                              </th>
                              <th className="px-3 py-2.5 text-center text-xs font-semibold text-emerald-600 uppercase" title="Pronto Pago — % individual o global" data-id="element-2394">
                                PP %
                              </th>
                              <th className="px-3 py-2.5 text-right text-xs font-semibold text-slate-500 uppercase w-36" data-id="element-2395">
                                Abono
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100" data-id="element-2396">
                            {clientInvoices.map(inv => {
                      const alloc = parseFloat(formData.paymentAllocations[inv.id] || '0');
                      const eligible = isDiscountEligible(inv);
                      const discApplied = !!formData.discountApplied[inv.id] && eligible;
                      const saldoReduction = getSaldoReduction(inv);
                      const discountAmount = getDiscountAmount(inv);
                      const newSaldo = Math.max(0, inv.saldoPendiente - saldoReduction);
                      const isFullyPaid = alloc > 0 && newSaldo === 0;
                      const hasPartial = alloc > 0 && newSaldo > 0;
                      const todayMs = new Date().setHours(0, 0, 0, 0);
                      const vencMs = new Date(inv.fechaVencimiento).setHours(0, 0, 0, 0);
                      const daysLeft = Math.max(0, Math.floor((vencMs - todayMs) / 86400000));
                      return <tr key={inv.id} className={isFullyPaid ? 'bg-emerald-50/50' : 'bg-white'} data-id="element-2397">
                                  <td className="px-3 py-3" data-id="element-2398">
                                    <div className="font-semibold text-slate-900" data-id="element-2399">
                                      {inv.numeroFactura}
                                    </div>
                                    <div className="text-xs text-slate-400 mt-0.5" data-id="element-2400">
                                      Vence:{' '}
                                      {new Date(inv.fechaVencimiento).toLocaleDateString('es-DO', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                                    </div>
                                    {eligible && <div className="text-xs text-emerald-600 font-semibold mt-0.5" data-id="element-2401">
                                        ⏱ {daysLeft}d para desc. (
                                        {getInvoicePP(inv)}%)
                                      </div>}
                                    {ncAppliedMap[inv.id] > 0 && <div className="text-xs text-blue-600 font-semibold mt-0.5" data-id="element-2402">
                                        NC: -RD$
                                        {ncAppliedMap[inv.id].toLocaleString()}{' '}
                                        → Sub: RD$
                                        {getAdjustedSubTotal(inv).toLocaleString()}
                                      </div>}
                                  </td>
                                  <td className="px-3 py-3 text-right" data-id="element-2403">
                                    <div className="font-bold text-slate-900" data-id="element-2404">
                                      RD${inv.saldoPendiente.toLocaleString()}
                                    </div>
                                    {isFullyPaid && <div className="text-xs text-emerald-600 font-semibold mt-0.5" data-id="element-2405">
                                        ✓ Pagada
                                      </div>}
                                    {hasPartial && <div className="text-xs text-amber-600 font-semibold mt-0.5" data-id="element-2406">
                                        Resta: RD${newSaldo.toLocaleString()}
                                      </div>}
                                    {discApplied && discountAmount > 0 && <div className="text-xs text-emerald-600 mt-0.5" data-id="element-2407">
                                        Desc: RD$
                                        {discountAmount.toLocaleString('es-DO', {
                              minimumFractionDigits: 2
                            })}
                                      </div>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-2408">
                                    {eligible ? <div className="flex flex-col items-center gap-1.5" data-id="element-2409">
                                        <label className="relative inline-flex items-center cursor-pointer" title={`Descuento pronto pago ${getInvoicePP(inv)}%`} data-id="element-2410">
                                          <input type="checkbox" checked={!!formData.discountApplied[inv.id]} onChange={e => toggleDiscount(inv.id, e.target.checked, inv)} className="sr-only peer" data-id="element-2411" />
                                          <div className="w-9 h-5 bg-slate-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500 relative" data-id="element-2412"></div>
                                        </label>
                                        <div className="relative" data-id="element-2413">
                                          <input type="number" min="0" max="100" step="0.5" value={formData.ppOverrides[inv.id] ?? (inv.ppPct != null ? String(inv.ppPct) : '')} onChange={e => setFormData(prev => ({
                                ...prev,
                                ppOverrides: {
                                  ...prev.ppOverrides,
                                  [inv.id]: e.target.value
                                }
                              }))} placeholder={`${discountPct}`} className="w-14 px-1 py-0.5 text-center text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500" title="% descuento para esta factura" data-id="element-2414" />
                                          <span className="absolute -right-3 top-1/2 -translate-y-1/2 text-[10px] text-slate-400" data-id="element-2415">
                                            %
                                          </span>
                                        </div>
                                      </div> : <span className="text-slate-300 text-xs" data-id="element-2416">
                                        —
                                      </span>}
                                  </td>
                                  <td className="px-3 py-3" data-id="element-2417">
                                    <div className="relative" data-id="element-2418">
                                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-medium pointer-events-none" data-id="element-2419">
                                        RD$
                                      </span>
                                      <input type="number" min="0" max={inv.saldoPendiente} placeholder="0" value={formData.paymentAllocations[inv.id] || ''} onChange={e => setFormData(prev => ({
                              ...prev,
                              paymentAllocations: {
                                ...prev.paymentAllocations,
                                [inv.id]: e.target.value
                              }
                            }))} className={`w-full pl-9 pr-2 py-2 text-right text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${discApplied ? 'border-emerald-300 bg-emerald-50/50' : 'border-slate-200'}`} data-id="element-2420" />
                                    </div>
                                    <button type="button" onClick={() => setFormData(prev => ({
                            ...prev,
                            paymentAllocations: {
                              ...prev.paymentAllocations,
                              [inv.id]: String(inv.saldoPendiente)
                            }
                          }))} className="mt-1.5 w-full text-xs text-emerald-600 hover:text-emerald-700 font-semibold text-right transition-colors" data-id="element-2421">
                                      Pagar total →
                                    </button>
                                  </td>
                                </tr>;
                    })}
                          </tbody>
                          <tfoot className="bg-emerald-50 border-t-2 border-emerald-200" data-id="element-2422">
                            <tr data-id="element-2423">
                              <td colSpan={3} className="px-3 py-3 text-right text-sm font-bold text-slate-700" data-id="element-2424">
                                Total a cobrar:
                              </td>
                              <td className="px-3 py-3 text-right text-base font-bold text-emerald-700" data-id="element-2425">
                                RD$
                                {totalCobrado.toLocaleString('es-DO', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Discount summary */}
                      {clientInvoices.some(inv => formData.discountApplied[inv.id] && isDiscountEligible(inv)) && <div className="mt-2 flex items-start gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl" data-id="element-2426">
                          <TagIcon className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-2427" />
                          <div className="text-xs text-emerald-700 font-semibold" data-id="element-2428">
                            {(() => {
                    const appliedInvs = clientInvoices.filter(inv => formData.discountApplied[inv.id] && isDiscountEligible(inv));
                    const pcts = [...new Set(appliedInvs.map(getInvoicePP))];
                    if (pcts.length === 1) {
                      return `Descuento por pronto pago (${pcts[0]}%) aplicado a ${appliedInvs.length} factura(s)`;
                    }
                    return <>
                                  Descuento PP aplicado a {appliedInvs.length}{' '}
                                  factura(s):
                                  {appliedInvs.map(inv => <span key={inv.id} className="block ml-1 font-normal" data-id="element-2429">
                                      • {inv.numeroFactura}: {getInvoicePP(inv)}
                                      %
                                    </span>)}
                                </>;
                  })()}
                          </div>
                        </div>}
                    </div> : <div className="space-y-4" data-id="element-2430">
                      <Input label="Número de Factura / Recibo" placeholder="Escriba el número de factura..." value={formData.numeroFacturaManual} onChange={e => setFormData({
                ...formData,
                numeroFacturaManual: e.target.value
              })} data-id="element-2431" />
                      <Input label="Monto Cobrado" type="number" placeholder="0.00" value={formData.paymentAllocations['__manual__'] || ''} onChange={e => setFormData(prev => ({
                ...prev,
                paymentAllocations: {
                  __manual__: e.target.value
                }
              }))} data-id="element-2432" />
                    </div>}

                  {formData.metodoPago === 'cheque' && <div className="space-y-4" data-id="element-2433">
                      {formData.cheques.map((cheque, chequeIdx) => <div key={chequeIdx} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-3" data-id="element-2434">
                          <div className="flex items-center justify-between" data-id="element-2435">
                            <span className="text-sm font-bold text-slate-700" data-id="element-2436">
                              Cheque #{chequeIdx + 1}
                            </span>
                            {formData.cheques.length > 1 && <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    cheques: prev.cheques.filter((_, i) => i !== chequeIdx)
                  }))} className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors" data-id="element-2437">
                                <TrashIcon className="w-4 h-4" data-id="element-2438" />
                              </button>}
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4" data-id="element-2439">
                            <Input label="Número de Cheque" placeholder="Ej: 001234" value={cheque.numeroCheque} onChange={e => setFormData(prev => {
                    const newCheques = [...prev.cheques];
                    newCheques[chequeIdx] = {
                      ...newCheques[chequeIdx],
                      numeroCheque: e.target.value
                    };
                    return {
                      ...prev,
                      cheques: newCheques
                    };
                  })} data-id="element-2440" />
                            <Input label="Banco" placeholder="Ej: Banreservas" value={cheque.bancoCheque} onChange={e => setFormData(prev => {
                    const newCheques = [...prev.cheques];
                    newCheques[chequeIdx] = {
                      ...newCheques[chequeIdx],
                      bancoCheque: e.target.value
                    };
                    return {
                      ...prev,
                      cheques: newCheques
                    };
                  })} data-id="element-2441" />
                            <Input label="Fecha de Depósito" type="date" value={cheque.fechaDeposito} onChange={e => setFormData(prev => {
                    const newCheques = [...prev.cheques];
                    newCheques[chequeIdx] = {
                      ...newCheques[chequeIdx],
                      fechaDeposito: e.target.value
                    };
                    return {
                      ...prev,
                      cheques: newCheques
                    };
                  })} data-id="element-2442" />
                          </div>
                          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-2443">
                            Imagen del Cheque
                          </label>
                          {!cheque.chequeImagen ? <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-colors" data-id="element-2444">
                              <ImageIcon className="w-8 h-8 text-slate-400 mb-2" data-id="element-2445" />
                              <span className="text-sm text-slate-500" data-id="element-2446">
                                Toca para adjuntar imagen del cheque
                              </span>
                              <span className="text-xs text-slate-400 mt-1" data-id="element-2447">
                                JPG, PNG, HEIC
                              </span>
                              <input type="file" accept="image/*" capture="environment" onChange={e => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = ev => setFormData(prev => {
                      const newCheques = [...prev.cheques];
                      newCheques[chequeIdx] = {
                        ...newCheques[chequeIdx],
                        chequeImagen: ev.target?.result as string
                      };
                      return {
                        ...prev,
                        cheques: newCheques
                      };
                    });
                    reader.readAsDataURL(file);
                  }} className="sr-only" data-id="element-2448" />
                            </label> : <div className="relative" data-id="element-2449">
                              <img src={cheque.chequeImagen} alt={`Cheque ${chequeIdx + 1}`} className="w-full h-40 object-cover rounded-xl border border-slate-200" data-id="element-2450" />
                              <button onClick={() => setFormData(prev => {
                    const newCheques = [...prev.cheques];
                    newCheques[chequeIdx] = {
                      ...newCheques[chequeIdx],
                      chequeImagen: null
                    };
                    return {
                      ...prev,
                      cheques: newCheques
                    };
                  })} className="absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-lg shadow-sm border border-slate-200 text-slate-600 hover:text-rose-600 transition-colors" data-id="element-2451">
                                <XIcon className="w-4 h-4" data-id="element-2452" />
                              </button>
                              <div className="absolute bottom-2 left-2 px-2 py-1 bg-emerald-500/90 rounded-lg" data-id="element-2453">
                                <span className="text-xs font-semibold text-white" data-id="element-2454">
                                  ✓ Imagen adjunta
                                </span>
                              </div>
                            </div>}
                        </div>)}
                      <button type="button" onClick={() => setFormData(prev => ({
                ...prev,
                cheques: [...prev.cheques, {
                  ...emptyCheque
                }]
              }))} className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-blue-300 rounded-xl text-sm font-semibold text-blue-600 hover:bg-blue-50 hover:border-blue-400 transition-colors" data-id="element-2455">
                        <PlusCircleIcon className="w-4 h-4" data-id="element-2456" />
                        Agregar otro cheque
                      </button>
                    </div>}
                </div>}

              {!formData.huboCobo && <Textarea label="Motivo de No Pago" placeholder="Explique por qué el cliente no realizó el pago..." value={formData.motivoNoPago} onChange={e => setFormData({
            ...formData,
            motivoNoPago: e.target.value
          })} rows={2} data-id="element-2457" />}
            </div>
          </div>}

        {/* Venta section */}
        {(currentVisit.tipoVisita === 'venta' || currentVisit.tipoVisita === 'cobro_y_venta') && <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-2458">
            <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-50 to-white border-b border-slate-100" data-id="element-2459">
              <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center" data-id="element-2460">
                <ShoppingCartIcon className="w-5 h-5 text-blue-600" data-id="element-2461" />
              </div>
              <div data-id="element-2462">
                <h3 className="font-bold text-slate-900" data-id="element-2463">Gestión de Venta</h3>
                <p className="text-xs text-slate-500" data-id="element-2464">
                  Registra la venta realizada
                </p>
              </div>
            </div>
            <div className="p-6 space-y-5" data-id="element-2465">
              <label className="flex items-center gap-3 cursor-pointer group" data-id="element-2466">
                <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${formData.huboVenta ? 'bg-blue-500 border-blue-500' : 'border-slate-300 group-hover:border-blue-400'}`} data-id="element-2467">
                  {formData.huboVenta && <CheckCircleIcon className="w-4 h-4 text-white" data-id="element-2468" />}
                  <input type="checkbox" checked={formData.huboVenta} onChange={e => setFormData({
                ...formData,
                huboVenta: e.target.checked
              })} className="sr-only" data-id="element-2469" />
                </div>
                <span className="font-semibold text-slate-800" data-id="element-2470">
                  ¿Se realizó una venta?
                </span>
              </label>
              {formData.huboVenta && <div className="space-y-4 pt-2" data-id="element-2471">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2472">
                    <Input label="Monto de Venta (con ITBIS)" type="number" placeholder="0.00" value={formData.montoVenta} onChange={e => setFormData({
                ...formData,
                montoVenta: e.target.value
              })} data-id="element-2473" />
                    <Input label="Número de Pedido" placeholder="PED-001" value={formData.numeroPedido} onChange={e => setFormData({
                ...formData,
                numeroPedido: e.target.value
              })} data-id="element-2474" />
                  </div>
                  {parseFloat(formData.montoVenta) > 0 && <div className="grid grid-cols-3 gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl text-xs sm:text-sm" data-id="element-2475">
                      <div className="min-w-0" data-id="element-2476">
                        <span className="block text-slate-500 text-[10px] sm:text-xs" data-id="element-2477">
                          Sub Total
                        </span>
                        <span className="font-bold text-slate-900 truncate block" data-id="element-2478">
                          RD$
                          {(Math.round(parseFloat(formData.montoVenta) / 1.18 * 100) / 100).toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                        </span>
                      </div>
                      <div className="min-w-0 text-center" data-id="element-2479">
                        <span className="block text-slate-500 text-[10px] sm:text-xs" data-id="element-2480">
                          ITBIS 18%
                        </span>
                        <span className="font-bold text-blue-700 truncate block" data-id="element-2481">
                          RD$
                          {(Math.round((parseFloat(formData.montoVenta) - parseFloat(formData.montoVenta) / 1.18) * 100) / 100).toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                        </span>
                      </div>
                      <div className="min-w-0 text-right" data-id="element-2482">
                        <span className="block text-slate-500 text-[10px] sm:text-xs" data-id="element-2483">
                          Total
                        </span>
                        <span className="font-bold text-blue-800 truncate block" data-id="element-2484">
                          RD$
                          {parseFloat(formData.montoVenta).toLocaleString('es-DO', {
                    minimumFractionDigits: 2
                  })}
                        </span>
                      </div>
                    </div>}
                </div>}
            </div>
          </div>}

        {/* Comments + next visit */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden" data-id="element-2485">
          <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100" data-id="element-2486">
            <div className="w-9 h-9 bg-slate-100 rounded-xl flex items-center justify-center" data-id="element-2487">
              <MessageSquareIcon className="w-5 h-5 text-slate-600" data-id="element-2488" />
            </div>
            <div data-id="element-2489">
              <h3 className="font-bold text-slate-900" data-id="element-2490">Observaciones</h3>
              <p className="text-xs text-slate-500" data-id="element-2491">
                Comentarios y próxima visita
              </p>
            </div>
          </div>
          <div className="p-6 space-y-4" data-id="element-2492">
            <Textarea label="Comentarios del Cliente" placeholder="Observaciones, solicitudes especiales, etc..." value={formData.comentarios} onChange={e => setFormData({
            ...formData,
            comentarios: e.target.value
          })} rows={3} data-id="element-2493" />
            <Input label="Fecha de Próxima Visita" type="date" value={formData.proximaVisita} min={new Date().toISOString().split('T')[0]} onChange={e => setFormData({
            ...formData,
            proximaVisita: e.target.value
          })} data-id="element-2494" />
          </div>
        </div>

        {/* Productos vencidos — obligatorio */}
        <div className="bg-white rounded-2xl border-2 border-amber-200 shadow-sm overflow-hidden" data-id="element-2495">
          <div className="flex items-center gap-3 px-6 py-4 bg-gradient-to-r from-amber-50 to-white border-b border-amber-100" data-id="element-2496">
            <div className="w-9 h-9 bg-amber-100 rounded-xl flex items-center justify-center" data-id="element-2497">
              <AlertCircleIcon className="w-5 h-5 text-amber-600" data-id="element-2498" />
            </div>
            <div data-id="element-2499">
              <h3 className="font-bold text-slate-900" data-id="element-2500">
                Productos Vencidos <span className="text-rose-500" data-id="element-2501">*</span>
              </h3>
              <p className="text-xs text-slate-500" data-id="element-2502">
                Campo obligatorio — responder antes de finalizar
              </p>
            </div>
          </div>
          <div className="p-6" data-id="element-2503">
            <p className="text-sm font-semibold text-slate-700 mb-4" data-id="element-2504">
              ¿El cliente tiene productos vencidos?
            </p>
            <div className="flex gap-3" data-id="element-2505">
              <button type="button" onClick={() => setFormData(prev => ({
              ...prev,
              productosVencidos: true
            }))} className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 transition-all ${formData.productosVencidos === true ? 'bg-rose-500 border-rose-500 text-white shadow-md' : 'border-slate-200 text-slate-600 hover:border-rose-300 hover:text-rose-600'}`} data-id="element-2506">
                ✓ Sí, tiene vencidos
              </button>
              <button type="button" onClick={() => setFormData(prev => ({
              ...prev,
              productosVencidos: false,
              devolucionItems: []
            }))} className={`flex-1 py-3 rounded-xl text-sm font-bold border-2 transition-all ${formData.productosVencidos === false ? 'bg-emerald-500 border-emerald-500 text-white shadow-md' : 'border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-600'}`} data-id="element-2507">
                ✗ No tiene vencidos
              </button>
            </div>
            {formData.productosVencidos === null && <p className="mt-3 text-xs text-amber-600 font-semibold flex items-center gap-1" data-id="element-2508">
                <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-2509" /> Debes seleccionar
                una opción para poder finalizar la visita
              </p>}

            {/* Devolucion section - shown when productosVencidos === true */}
            {formData.productosVencidos === true && <div className="mt-6 pt-5 border-t border-amber-100" data-id="element-2510">
                <div className="flex items-center gap-2 mb-4" data-id="element-2511">
                  <PackageIcon className="w-4 h-4 text-rose-500" data-id="element-2512" />
                  <span className="text-sm font-bold text-slate-800" data-id="element-2513">
                    Registrar Productos para Devolución
                  </span>
                </div>

                {/* Product search */}
                <div className="relative mb-4" data-id="element-2514">
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2515">
                    Buscar producto en inventario
                  </label>
                  <div className="relative" data-id="element-2516">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-2517">
                      <SearchIcon className="w-4 h-4" data-id="element-2518" />
                    </span>
                    <input type="text" autoComplete="off" value={devolucionSearch} onChange={e => {
                  setDevolucionSearch(e.target.value);
                  setShowDevolucionDropdown(true);
                }} onFocus={() => setShowDevolucionDropdown(true)} placeholder={isLoadingProducts ? 'Cargando productos...' : 'Escriba nombre o código del producto...'} disabled={isLoadingProducts} className="w-full pl-9 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-rose-400 transition-colors bg-white disabled:bg-slate-50" data-id="element-2519" />
                    {devolucionSearch && <button onClick={() => {
                  setDevolucionSearch('');
                  setShowDevolucionDropdown(false);
                }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg leading-none" data-id="element-2520">
                        ×
                      </button>}
                  </div>
                  {showDevolucionDropdown && !isLoadingProducts && <div className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-60 overflow-y-auto" data-id="element-2521">
                      {(() => {
                  const query = devolucionSearch.toLowerCase().trim();
                  const addedIds = formData.devolucionItems.map(d => d.productoId);
                  // Sort products: promoted ones first, then alphabetical
                  const sortedProducts = [...inventoryProducts].sort((a, b) => {
                    const isPromoA = promotions.some(p => p.productId === a.id);
                    const isPromoB = promotions.some(p => p.productId === b.id);
                    if (isPromoA && !isPromoB) return -1;
                    if (!isPromoA && isPromoB) return 1;
                    return a.nombre.localeCompare(b.nombre);
                  });
                  const filtered = sortedProducts.filter(p => !addedIds.includes(p.id)).filter(p => query.length === 0 || p.nombre.toLowerCase().includes(query) || p.sku.toLowerCase().includes(query)).slice(0, 50);
                  if (filtered.length === 0) {
                    return <div className="px-4 py-6 text-center text-sm text-slate-400" data-id="element-2522">
                              {query ? 'No se encontró el producto' : 'Todos los productos ya fueron agregados'}
                            </div>;
                  }
                  return filtered.map(p => {
                    const promo = promotions.find(pr => pr.productId === p.id);
                    return <button key={p.id} onClick={() => {
                      // Apply promotion discount to price if available
                      let finalPrice = p.precio || 0;
                      if (promo && promo.activo) {
                        if (promo.precioOferta > 0) {
                          finalPrice = promo.precioOferta;
                        } else if (promo.descuentoPct > 0) {
                          finalPrice = Math.round(finalPrice * (1 - promo.descuentoPct / 100) * 100) / 100;
                        }
                      }
                      setFormData(prev => ({
                        ...prev,
                        devolucionItems: [...prev.devolucionItems, {
                          productoId: p.id,
                          productoNombre: p.nombre,
                          productoSku: p.sku,
                          cantidad: 0,
                          precioUnitario: finalPrice,
                          lote: '',
                          fechaVencimiento: ''
                        }]
                      }));
                      setDevolucionSearch('');
                      setShowDevolucionDropdown(false);
                    }} className="w-full text-left px-4 py-3 hover:bg-rose-50 transition-colors border-b border-slate-100 last:border-0 flex items-start gap-3" data-id="element-2523">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${promo ? promo.tipo === 'oferta' ? 'bg-emerald-100' : 'bg-amber-100' : 'bg-rose-100'}`} data-id="element-2524">
                                <PackageIcon className={`w-4 h-4 ${promo ? promo.tipo === 'oferta' ? 'text-emerald-600' : 'text-amber-600' : 'text-rose-600'}`} data-id="element-2525" />
                              </div>
                              <div className="min-w-0 flex-1" data-id="element-2526">
                                <div className="flex items-center gap-2" data-id="element-2527">
                                  <p className="font-semibold text-slate-900 text-sm truncate" data-id="element-2528">
                                    {p.nombre}
                                  </p>
                                  {promo && <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${promo.tipo === 'oferta' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-2529">
                                      {promo.tipo}
                                    </span>}
                                </div>
                                <div className="flex items-center gap-2" data-id="element-2530">
                                  <p className="text-xs text-slate-400 font-mono" data-id="element-2531">
                                    {p.sku}
                                  </p>
                                  {promo && promo.activo && (promo.descuentoPct > 0 || promo.precioOferta > 0) && <span className="text-xs font-bold text-emerald-600" data-id="element-2532">
                                        · RD$
                                        {(promo.precioOferta > 0 ? promo.precioOferta : Math.round((p.precio || 0) * (1 - promo.descuentoPct / 100) * 100) / 100).toLocaleString('es-DO', {
                              minimumFractionDigits: 2
                            })}
                                        {promo.descuentoPct > 0 && ` (-${promo.descuentoPct}%)`}
                                      </span>}
                                </div>
                              </div>
                            </button>;
                  });
                })()}
                    </div>}
                  {showDevolucionDropdown && <div className="fixed inset-0 z-[9998]" onClick={() => setShowDevolucionDropdown(false)} data-id="element-2533" />}
                </div>

                {/* Added products list */}
                {formData.devolucionItems.length > 0 && <div className="space-y-3" data-id="element-2534">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide" data-id="element-2535">
                      Productos agregados ({formData.devolucionItems.length})
                    </p>
                    {formData.devolucionItems.map((item, idx) => <div key={item.productoId} className="p-4 bg-rose-50 border border-rose-200 rounded-xl" data-id="element-2536">
                        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-2537">
                          <div className="flex items-center gap-2" data-id="element-2538">
                            <div className="w-8 h-8 rounded-lg bg-rose-100 flex items-center justify-center flex-shrink-0" data-id="element-2539">
                              <PackageIcon className="w-4 h-4 text-rose-600" data-id="element-2540" />
                            </div>
                            <div data-id="element-2541">
                              <p className="font-semibold text-slate-900 text-sm" data-id="element-2542">
                                {item.productoNombre}
                              </p>
                              <p className="text-xs text-slate-500 font-mono" data-id="element-2543">
                                {item.productoSku}
                              </p>
                            </div>
                          </div>
                          <button type="button" onClick={() => {
                    setFormData(prev => ({
                      ...prev,
                      devolucionItems: prev.devolucionItems.filter((_, i) => i !== idx)
                    }));
                  }} className="p-1.5 text-rose-400 hover:text-rose-600 hover:bg-rose-100 rounded-lg transition-colors" data-id="element-2544">
                            <TrashIcon className="w-4 h-4" data-id="element-2545" />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-3" data-id="element-2546">
                          <div className="min-w-0" data-id="element-2547">
                            <label className="block text-xs font-semibold text-slate-600 mb-1" data-id="element-2548">
                              Cantidad
                            </label>
                            <input type="number" min="1" placeholder="0" value={item.cantidad || ''} onChange={e => {
                      const newItems = [...formData.devolucionItems];
                      newItems[idx] = {
                        ...newItems[idx],
                        cantidad: parseInt(e.target.value) || 0
                      };
                      setFormData(prev => ({
                        ...prev,
                        devolucionItems: newItems
                      }));
                    }} className="w-full px-3 py-2 text-sm border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 bg-white" data-id="element-2549" />
                          </div>
                          <div data-id="element-2550">
                            <label className="block text-xs font-semibold text-slate-600 mb-1" data-id="element-2551">
                              Precio Unit.
                            </label>
                            <div className="relative" data-id="element-2552">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none" data-id="element-2553">
                                RD$
                              </span>
                              <input type="number" min="0" step="0.01" placeholder="0.00" value={item.precioUnitario || ''} onChange={e => {
                        const newItems = [...formData.devolucionItems];
                        newItems[idx] = {
                          ...newItems[idx],
                          precioUnitario: parseFloat(e.target.value) || 0
                        };
                        setFormData(prev => ({
                          ...prev,
                          devolucionItems: newItems
                        }));
                      }} className="w-full pl-8 pr-2 py-2 text-sm text-right border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 bg-white" data-id="element-2554" />
                            </div>
                          </div>
                          <div data-id="element-2555">
                            <label className="block text-xs font-semibold text-slate-600 mb-1" data-id="element-2556">
                              Lote
                            </label>
                            <input type="text" placeholder="Ej: LOT-001" value={item.lote} onChange={e => {
                      const newItems = [...formData.devolucionItems];
                      newItems[idx] = {
                        ...newItems[idx],
                        lote: e.target.value
                      };
                      setFormData(prev => ({
                        ...prev,
                        devolucionItems: newItems
                      }));
                    }} className="w-full px-3 py-2 text-sm border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 bg-white" data-id="element-2557" />
                          </div>
                          <div data-id="element-2558">
                            <label className="block text-xs font-semibold text-slate-600 mb-1" data-id="element-2559">
                              F. Vencimiento
                            </label>
                            <input type="date" value={item.fechaVencimiento} onChange={e => {
                      const newItems = [...formData.devolucionItems];
                      newItems[idx] = {
                        ...newItems[idx],
                        fechaVencimiento: e.target.value
                      };
                      setFormData(prev => ({
                        ...prev,
                        devolucionItems: newItems
                      }));
                    }} className="w-full px-3 py-2 text-sm border border-rose-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-rose-400 bg-white" data-id="element-2560" />
                          </div>
                        </div>
                        {item.cantidad > 0 && item.precioUnitario > 0 && <div className="mt-2 flex items-center justify-end gap-1 text-sm" data-id="element-2561">
                            <span className="text-slate-500" data-id="element-2562">Valor:</span>
                            <span className="font-bold text-rose-700" data-id="element-2563">
                              RD$
                              {(item.cantidad * item.precioUnitario).toLocaleString('es-DO', {
                      minimumFractionDigits: 2
                    })}
                            </span>
                          </div>}
                      </div>)}
                  </div>}

                {formData.devolucionItems.length === 0 && <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700" data-id="element-2564">
                    <AlertCircleIcon className="w-4 h-4 flex-shrink-0" data-id="element-2565" />
                    <span data-id="element-2566">
                      Busca y agrega los productos vencidos para registrar la
                      devolución
                    </span>
                  </div>}

                {/* Total devolucion value */}
                {formData.devolucionItems.length > 0 && (() => {
              const totalDevVal = formData.devolucionItems.reduce((sum, item) => sum + item.cantidad * (item.precioUnitario || 0), 0);
              return totalDevVal > 0 ? <div className="mt-4 p-4 bg-rose-100 border border-rose-300 rounded-xl" data-id="element-2567">
                        <div className="flex items-center justify-between" data-id="element-2568">
                          <div className="flex items-center gap-2" data-id="element-2569">
                            <DollarSignIcon className="w-5 h-5 text-rose-600" data-id="element-2570" />
                            <span className="text-sm font-bold text-rose-800" data-id="element-2571">
                              Valor total de devolución
                            </span>
                          </div>
                          <span className="text-lg font-black text-rose-700" data-id="element-2572">
                            RD$
                            {totalDevVal.toLocaleString('es-DO', {
                      minimumFractionDigits: 2
                    })}
                          </span>
                        </div>
                        <p className="text-xs text-rose-600 mt-1.5" data-id="element-2573">
                          Se generará una Nota de Crédito por este monto al
                          finalizar la visita
                        </p>
                      </div> : null;
            })()}
              </div>}
          </div>
        </div>

        {/* End visit */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5" data-id="element-2574">
          <Button onClick={handleEndVisit} variant="danger" size="lg" className="w-full" isLoading={isGettingLocation} leftIcon={<StopCircleIcon className="w-5 h-5" data-id="element-2576" />} data-id="element-2575">
            Finalizar Visita
          </Button>
        </div>

        {/* Abandon confirm */}
        {showAbandonConfirm && <>
            <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setShowAbandonConfirm(false)} data-id="element-2577" />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-2578">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6" data-id="element-2579">
                <div className="flex items-center justify-center w-14 h-14 bg-amber-100 rounded-2xl mx-auto mb-4" data-id="element-2580">
                  <AlertCircleIcon className="w-7 h-7 text-amber-600" data-id="element-2581" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 text-center mb-2" data-id="element-2582">
                  ¿Qué deseas hacer?
                </h3>
                <p className="text-sm text-slate-500 text-center mb-6" data-id="element-2583">
                  Visita de <strong data-id="element-2584">{currentVisit.clienteNombre}</strong>
                </p>

                <div className="space-y-3" data-id="element-2585">
                  {/* Option 1: Leave without saving (keeps visit en_progreso) */}
                  <button onClick={handleAbandonVisit} className="w-full flex flex-col items-start px-4 py-3.5 text-left bg-amber-50 hover:bg-amber-100 border border-amber-200 rounded-xl transition-colors" data-id="element-2586">
                    <span className="text-sm font-bold text-amber-800" data-id="element-2587">
                      Salir sin guardar
                    </span>
                    <span className="text-xs text-amber-600 mt-0.5" data-id="element-2588">
                      La visita queda "En Progreso". Puedes retomar más tarde.
                    </span>
                  </button>

                  {/* Option 2: Close definitively (marks as cancelada in DB) */}
                  <button onClick={handleCloseDefinitively} className="w-full flex flex-col items-start px-4 py-3.5 text-left bg-rose-50 hover:bg-rose-100 border border-rose-200 rounded-xl transition-colors" data-id="element-2589">
                    <span className="text-sm font-bold text-rose-700" data-id="element-2590">
                      Cerrar definitivamente
                    </span>
                    <span className="text-xs text-rose-500 mt-0.5" data-id="element-2591">
                      La visita se marca como cancelada. No se puede retomar.
                    </span>
                  </button>

                  {/* Cancel */}
                  <button onClick={() => setShowAbandonConfirm(false)} className="w-full px-4 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors" data-id="element-2592">
                    Volver a la visita
                  </button>
                </div>
              </div>
            </div>
          </>}
      </div>;
  }
  // ── START VISIT VIEW ───────────────────────────────────────────────────────
  return <div className="max-w-2xl mx-auto pb-10 space-y-5" data-id="element-2593">
      {/* ── Hero Header ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-slate-900 shadow-2xl" data-id="element-2594">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle at 15% 50%, rgba(99,102,241,0.4) 0%, transparent 55%), radial-gradient(circle at 85% 20%, rgba(16,185,129,0.25) 0%, transparent 45%)'
      }} data-id="element-2595" />
        <div className="relative px-5 py-6 sm:px-8 sm:py-9 flex items-center gap-4 sm:gap-5" data-id="element-2596">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center flex-shrink-0 shadow-lg" data-id="element-2597">
            <NavigationIcon className="w-6 h-6 sm:w-8 sm:h-8 text-white" data-id="element-2598" />
          </div>
          <div className="min-w-0" data-id="element-2599">
            <p className="text-indigo-300 text-[10px] sm:text-xs font-bold uppercase tracking-widest mb-1" data-id="element-2600">
              Gestión de Visitas
            </p>
            <h1 className="text-xl sm:text-2xl font-black text-white tracking-tight" data-id="element-2601">
              Nueva Visita
            </h1>
            <p className="text-slate-400 text-xs sm:text-sm mt-0.5 flex items-center gap-2" data-id="element-2602">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" data-id="element-2603" />
              {new Date().toLocaleDateString('es-DO', {
              weekday: 'long',
              day: 'numeric',
              month: 'long'
            })}
            </p>
          </div>
        </div>
      </div>

      {locationError && <div className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 rounded-2xl text-sm text-rose-700" data-id="element-2604">
          <AlertCircleIcon className="w-5 h-5 flex-shrink-0" data-id="element-2605" />
          {locationError}
        </div>}

      {/* ── Paso 1: Tipo de Visita ──────────────────────────────────────────── */}
      <div data-id="element-2606">
        <div className="flex items-center gap-2 mb-3 px-1" data-id="element-2607">
          <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center" data-id="element-2608">
            1
          </span>
          <span className="text-sm font-bold text-slate-600 uppercase tracking-wide" data-id="element-2609">
            Tipo de visita
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3" data-id="element-2610">
          {([{
          value: 'cobro',
          label: 'Cobro',
          icon: '💰',
          desc: 'Cobro de facturas',
          active: 'bg-emerald-500 border-emerald-500',
          badge: 'bg-emerald-100 text-emerald-700'
        }, {
          value: 'venta',
          label: 'Venta',
          icon: '🛒',
          desc: 'Toma de pedido',
          active: 'bg-blue-500 border-blue-500',
          badge: 'bg-blue-100 text-blue-700'
        }, {
          value: 'cobro_y_venta',
          label: 'Cobro y Venta',
          icon: '⚡',
          desc: 'Cobro + pedido',
          active: 'bg-indigo-500 border-indigo-500',
          badge: 'bg-indigo-100 text-indigo-700'
        }, {
          value: 'seguimiento',
          label: 'Seguimiento',
          icon: '📋',
          desc: 'Visita de seguimiento',
          active: 'bg-amber-500 border-amber-500',
          badge: 'bg-amber-100 text-amber-700'
        }] as const).map(card => {
          const isSel = visitType === card.value;
          return <button key={card.value} onClick={() => {
            setVisitType(card.value as VisitType);
            if (card.value !== 'seguimiento') setIsNewClient(false);
          }} className={`flex flex-col items-start gap-1.5 px-4 py-4 rounded-2xl border-2 text-left transition-all duration-200 shadow-sm ${isSel ? `${card.active} scale-[1.02] shadow-lg` : 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-md'}`} data-id="element-2611">
                <div className="flex items-center justify-between w-full" data-id="element-2612">
                  <span className="text-2xl" data-id="element-2613">{card.icon}</span>
                  {isSel && <CheckCircleIcon className="w-4 h-4 text-white/80" data-id="element-2614" />}
                </div>
                <span className={`font-bold text-sm ${isSel ? 'text-white' : 'text-slate-800'}`} data-id="element-2615">
                  {card.label}
                </span>
                <span className={`text-xs ${isSel ? 'text-white/70' : 'text-slate-400'}`} data-id="element-2616">
                  {card.desc}
                </span>
              </button>;
        })}
        </div>
      </div>

      {/* ── Paso 2: Cliente ─────────────────────────────────────────────────── */}
      <div data-id="element-2617">
        <div className="flex items-center gap-2 mb-3 px-1" data-id="element-2618">
          <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center" data-id="element-2619">
            2
          </span>
          <span className="text-sm font-bold text-slate-600 uppercase tracking-wide" data-id="element-2620">
            Seleccionar cliente
          </span>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm" data-id="element-2621">
          <div className="p-5 space-y-4" data-id="element-2622">
            {/* Nuevo cliente toggle — solo seguimiento */}
            {visitType === 'seguimiento' && <label className="flex items-center gap-3 cursor-pointer p-3 rounded-xl bg-amber-50 border border-amber-200 hover:bg-amber-100 transition-colors" data-id="element-2623">
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${isNewClient ? 'bg-amber-500 border-amber-500' : 'border-amber-300'}`} data-id="element-2624">
                  {isNewClient && <CheckCircleIcon className="w-3.5 h-3.5 text-white" data-id="element-2625" />}
                  <input type="checkbox" checked={isNewClient} onChange={e => {
                setIsNewClient(e.target.checked);
                if (e.target.checked) setSelectedClient('');
              }} className="sr-only" data-id="element-2626" />
                </div>
                <PlusCircleIcon className="w-4 h-4 text-amber-600" data-id="element-2627" />
                <span className="font-semibold text-amber-800 text-sm" data-id="element-2628">
                  Nuevo Cliente (no está en la lista)
                </span>
              </label>}

            {isNewClient ? <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3" data-id="element-2629">
                <p className="text-xs text-amber-700 font-semibold" data-id="element-2630">
                  Se creará un nuevo cliente al iniciar la visita
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-2631">
                  <Input label="Código" placeholder="CLI-001" value={newClientCodigo} onChange={e => setNewClientCodigo(e.target.value)} data-id="element-2632" />
                  <Input label="Nombre de la Farmacia *" placeholder="Farmacia Central" value={newClientName} onChange={e => setNewClientName(e.target.value)} data-id="element-2633" />
                  <Input label="RNC" placeholder="101-12345-6" value={newClientRnc} onChange={e => setNewClientRnc(e.target.value)} data-id="element-2634" />
                  <Input label="Cédula" placeholder="001-1234567-8" value={newClientCedula} onChange={e => setNewClientCedula(e.target.value)} data-id="element-2635" />
                  <Input label="Teléfono" placeholder="809-555-1234" value={newClientTelefono} onChange={e => setNewClientTelefono(e.target.value)} data-id="element-2636" />
                  <Input label="Ciudad" placeholder="Santo Domingo" value={newClientCiudad} onChange={e => setNewClientCiudad(e.target.value)} data-id="element-2637" />
                  <Input label="Ruta" placeholder="Ruta 1" value={newClientRuta} onChange={e => setNewClientRuta(e.target.value)} data-id="element-2638" />
                  <Input label="Ruta Zona" placeholder="Zona Colonial" value={newClientZona} onChange={e => setNewClientZona(e.target.value)} data-id="element-2639" />
                  <Input label="Límite de Crédito (DOP)" type="number" placeholder="0.00" value={newClientLimite} onChange={e => setNewClientLimite(e.target.value)} data-id="element-2640" />
                  <Input label="Dependencia" placeholder="Casa Matriz" value={newClientDependencia} onChange={e => setNewClientDependencia(e.target.value)} data-id="element-2641" />
                </div>
                <Input label="Dirección" placeholder="Calle El Conde #123" value={newClientDireccion} onChange={e => setNewClientDireccion(e.target.value)} data-id="element-2642" />
              </div> : <div className="relative" data-id="element-2643">
                <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2644">
                  Buscar farmacia
                </label>
                <div className="relative" data-id="element-2645">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-2646">
                    🔍
                  </span>
                  <input type="text" autoComplete="off" value={selectedClient ? clients.find(c => c.id === selectedClient)?.nombre || clientSearch : clientSearch} onChange={e => {
                setClientSearch(e.target.value);
                setSelectedClient('');
                setShowClientDropdown(true);
              }} onFocus={() => {
                if (selectedClient) setClientSearch('');
                setShowClientDropdown(true);
              }} placeholder="Escriba el nombre o zona..." className="w-full pl-9 pr-4 py-3 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-400 transition-colors bg-white" data-id="element-2647" />
                  {(clientSearch || selectedClient) && <button onClick={() => {
                setClientSearch('');
                setSelectedClient('');
                setShowClientDropdown(false);
              }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-lg leading-none" data-id="element-2648">
                      ×
                    </button>}
                </div>
                {showClientDropdown && <div className="absolute z-[9999] w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl max-h-72 overflow-y-auto" data-id="element-2649">
                    {(() => {
                const query = clientSearch.toLowerCase().trim();
                const filtered = query.length === 0 ? clients : clients.filter(c => c.nombre?.toLowerCase().includes(query) || c.zona && c.zona !== 'null' && c.zona.toLowerCase().includes(query) || (c as any).ruta && (c as any).ruta.toLowerCase().includes(query));
                if (filtered.length === 0) {
                  return <div className="px-4 py-6 text-center text-sm text-slate-400" data-id="element-2650">
                            No se encontró ninguna farmacia
                          </div>;
                }
                return filtered.map(c => <button key={c.id} onClick={() => {
                  setSelectedClient(c.id);
                  setClientSearch('');
                  setShowClientDropdown(false);
                  setIsNewClient(false);
                }} className={`w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors border-b border-slate-100 last:border-0 flex items-start gap-3 ${selectedClient === c.id ? 'bg-indigo-50' : ''}`} data-id="element-2651">
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-2652">
                            <BuildingIcon className="w-4 h-4 text-indigo-600" data-id="element-2653" />
                          </div>
                          <div className="min-w-0" data-id="element-2654">
                            <p className="font-semibold text-slate-900 text-sm truncate" data-id="element-2655">
                              {c.nombre}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-0.5" data-id="element-2656">
                              {c.zona && c.zona !== 'null' && <span className="text-xs text-indigo-600 font-medium" data-id="element-2657">
                                  {c.zona}
                                </span>}
                              {(c as any).ruta && <span className="text-xs text-slate-400" data-id="element-2658">
                                  · {(c as any).ruta}
                                </span>}
                              {c.telefono && <span className="text-xs text-slate-400" data-id="element-2659">
                                  · {c.telefono}
                                </span>}
                            </div>
                          </div>
                          {selectedClient === c.id && <CheckCircleIcon className="w-4 h-4 text-indigo-500 flex-shrink-0 ml-auto mt-1" data-id="element-2660" />}
                        </button>);
              })()}
                  </div>}
                {showClientDropdown && <div className="fixed inset-0 z-[9998]" onClick={() => setShowClientDropdown(false)} data-id="element-2661" />}
              </div>}

            {/* Card preview del cliente seleccionado */}
            {selectedClientData && !isNewClient && <div className="relative overflow-hidden rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-4" data-id="element-2662">
                <div className="absolute top-0 right-0 w-24 h-24 rounded-full bg-indigo-100/40 -translate-y-8 translate-x-8" data-id="element-2663" />
                <div className="relative flex items-start gap-3" data-id="element-2664">
                  <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-2665">
                    <BuildingIcon className="w-5 h-5 text-indigo-600" data-id="element-2666" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-2667">
                    <p className="font-bold text-slate-900 text-sm" data-id="element-2668">
                      {selectedClientData.nombre}
                    </p>
                    {selectedClientData.direccion && <p className="text-xs text-slate-500 mt-0.5 truncate" data-id="element-2669">
                        {selectedClientData.direccion}
                      </p>}
                    <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-2670">
                      {selectedClientData.zona && selectedClientData.zona !== 'null' && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-100 text-indigo-700 text-xs font-semibold rounded-full" data-id="element-2671">
                            <MapPinIcon className="w-3 h-3" data-id="element-2672" />
                            {selectedClientData.zona}
                          </span>}
                      {selectedClientData.telefono && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full" data-id="element-2673">
                          <PhoneIcon className="w-3 h-3" data-id="element-2674" />
                          {selectedClientData.telefono}
                        </span>}
                      {selectedClientData.contacto && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full" data-id="element-2675">
                          <UserIcon className="w-3 h-3" data-id="element-2676" />
                          {selectedClientData.contacto}
                        </span>}
                    </div>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>

      {/* ── Resumen antes de iniciar ─────────────────────────────────────────── */}
      {(selectedClient || isNewClient && newClientName.trim()) && <div className="p-4 rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/60" data-id="element-2677">
          <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider mb-2" data-id="element-2678">
            Resumen
          </p>
          <div className="flex items-center gap-2 flex-wrap" data-id="element-2679">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-bold ${visitTypeConfig[visitType].bg} ${visitTypeConfig[visitType].color}`} data-id="element-2680">
              {visitTypeConfig[visitType].label}
            </span>
            <ChevronRightIcon className="w-4 h-4 text-indigo-300" data-id="element-2681" />
            <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-2682">
              {isNewClient ? newClientName : selectedClientData?.nombre || '—'}
            </span>
          </div>
        </div>}

      {/* ── Botón Iniciar ───────────────────────────────────────────────────── */}
      <Button onClick={handleStartVisit} size="lg" className="w-full !rounded-2xl !py-5 !text-base !font-black !shadow-xl !shadow-indigo-200 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-500 hover:to-indigo-600 transition-all hover:scale-[1.01] active:scale-[0.99]" disabled={isNewClient ? !newClientName.trim() : !selectedClient} isLoading={isCreatingClient} leftIcon={<PlayIcon className="w-5 h-5" data-id="element-2684" />} data-id="element-2683">
        {isCreatingClient ? 'Creando cliente...' : 'Iniciar Visita'}
      </Button>

      {/* ── Tips ────────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-3 gap-3" data-id="element-2685">
        {[{
        icon: '📍',
        text: 'Finaliza la visita al salir'
      }, {
        icon: '💳',
        text: 'Registra todos los cobros'
      }, {
        icon: '📝',
        text: 'Agrega comentarios'
      }].map((tip, i) => <div key={i} className="flex flex-col items-center gap-2 p-3 bg-white border border-slate-100 rounded-2xl text-center shadow-sm" data-id="element-2686">
            <span className="text-xl" data-id="element-2687">{tip.icon}</span>
            <p className="text-xs text-slate-500 leading-tight" data-id="element-2688">{tip.text}</p>
          </div>)}
      </div>
    </div>;
}